import React, { useState, useEffect } from "react";
import { Upload, Button, message, Modal } from "antd";
import ImgCrop from "antd-img-crop";
import { UploadOutlined, DeleteOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { color } from "../../resource";
import Text from "../Text/Text";

const ContentArea = styled.div`
  width: 100%;
  height: 80px;
  background: ${color.background1};
  border: 1px solid ${color.background2};
  border-radius: 8px;
  display: flex;
  align-items: center;
  padding: 16px;
`;
const ContentAreaItem = styled.div`
  padding: 0px 8px;
  display: flex;
  align-items: center;
`;

interface UploadComponentProps {
  onFileUpload: (file: File | null) => void;
  value: File | string | null;
  accept?: "image" | "pdf";
  aspect?: number;
}

const UploadImageCropper: React.FC<UploadComponentProps> = ({
  onFileUpload,
  value,
  accept = "image",
  aspect = 1,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [fileURL, setFileURL] = useState<string | null>(null);
  const [previewVisible, setPreviewVisible] = useState(false);

  useEffect(() => {
    if (value) {
      if (typeof value === "string") {
        setFileURL(value);
        setFile(null);
      } else {
        const url = URL.createObjectURL(value);
        setFileURL(url);
        setFile(value);
      }
    } else {
      setFile(null);
      setFileURL(null);
    }
  }, [value]);
  useEffect(() => {
    return () => {
      if (fileURL) {
        URL.revokeObjectURL(fileURL);
      }
    };
  }, [fileURL]);

  const extractFilename = (url: string): string | null => {
    const match = url.match(/sign_\d{4}-\d{2}-\d{2}T/);
    return match ? match[0] + ".png" : null;
  };

  const beforeUpload = (file: File) => {
    const isPng = file.type === "image/png";
    if (!isPng) {
      message.error("โปรดเลือกไฟล์รูป .PNG");
      return Upload.LIST_IGNORE;
    }
    return true;
  };
  const imgCropProps = {
    modalTitle: "ปรับขนาดรูปให้เหมาะสม",
    modalOk: "ยืนยัน",
    modalCancel: "ยกเลิก",
  };
  const handleRemove = () => {
    Modal.confirm({
      title: "ลบไฟล์รูปภาพ",
      onOk: () => {
        setFile(null);
        setFileURL(null);
        onFileUpload(null);
      },
    });
  };

  return (
    <div style={{ margin: "16px 0px 0px 0px" }}>
      <ContentArea>
        <ContentAreaItem>
          <ImgCrop aspectSlider aspect={aspect} showGrid {...imgCropProps}>
            <Upload
              beforeUpload={beforeUpload}
              customRequest={({ file, onSuccess }) => {
                const url = URL.createObjectURL(file as File);
                setFile(file as File);
                setFileURL(url);
                onFileUpload(file as File);
                if (onSuccess) onSuccess("done");
              }}
              showUploadList={false}
            >
              <Button type='primary' disabled={!!file} style={{ height: "40px", width: "135px" }}>
                <UploadOutlined /> เลือกไฟล์
              </Button>
            </Upload>
          </ImgCrop>
        </ContentAreaItem>
        <ContentAreaItem style={{ flex: 1 }}>
          {(typeof value === "string" && value) || file ? (
            <a href={fileURL!} target='_blank' rel='noopener noreferrer'>
              <Text
                fontFamily='Sarabun'
                fontSize={14}
                color='primary'
                style={{ textDecoration: "underline" }}
              >
                {typeof value === "string" ? extractFilename(value) : file?.name}
              </Text>
            </a>
          ) : (
            <Text fontFamily='Sarabun' fontSize={16} color='background4'>
              โปรดเลือกไฟล์ .PNG
            </Text>
          )}
        </ContentAreaItem>
        {(file || fileURL) && (
          <ContentAreaItem>
            <DeleteOutlined
              style={{ color: color.error, cursor: "pointer" }}
              onClick={handleRemove}
            />
          </ContentAreaItem>
        )}
      </ContentArea>
      <Modal open={previewVisible} footer={null} onCancel={() => setPreviewVisible(false)}>
        {fileURL && accept === "image" && (
          <img alt='preview' style={{ width: "100%" }} src={fileURL} />
        )}
      </Modal>
    </div>
  );
};

export default UploadImageCropper;
